/**
 * The liquidity floors, the review ceiling, and the two switches that stop the desk.
 *
 * Every field here changes what the *automated* gate does without asking anybody, so the page
 * is deliberately plain about consequence rather than compact. Two of the controls are not
 * like the others:
 *
 * - **Sandbox** decides whether a conversion calls a custodian at all. Turning it off is the
 *   moment this platform starts trading real coin, and turning it on again makes every
 *   subsequent order settle against nothing.
 * - **Pause conversions** stops the machine for everybody, immediately.
 *
 * The three money fields are typed in major units and sent in minor, and each is pinned to its
 * currency. That is not cosmetic: the API stores them as bare integers whose currency is
 * implied by *which* threshold it is, so a figure in the wrong currency is silently
 * reinterpreted rather than rejected — a hundred USDT written into the naira float threshold
 * becomes ₦1.00 and the low-float alarm stops firing. The currency is fixed by the form and
 * checked again by the API.
 */
import { useEffect, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { SetTreasuryThresholdsRequest, TreasuryThresholds } from '../contracts/types.js';
import { Button, Card, ErrorBox, Field, Input, LoadingState, Switch } from '../components/ui.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { decimalsOf, minorUnitsToInput, parseMinorUnits } from '../lib/money.js';

export function TreasuryThresholdsPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'rates:write');
  const state = useAsync(() => api.treasuryThresholds(), []);

  const [form, setForm] = useState<TreasuryThresholds | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state.data !== null) setForm(state.data);
  }, [state.data]);

  if (state.loading) return <LoadingState label="Loading" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (form === null) return <ErrorBox message="Could not load your limits." />;

  function set<K extends keyof TreasuryThresholds>(key: K, value: TreasuryThresholds[K]): void {
    setForm((prev) => (prev === null ? prev : { ...prev, [key]: value }));
  }

  function setMoney(key: 'usdtLowThreshold' | 'ngnFloatLowThreshold' | 'manualReviewCeiling', typed: string): void {
    setForm((prev) => {
      if (prev === null) return prev;
      const currency = prev[key].currency;
      const minor = parseMinorUnits(typed, decimalsOf(currency));
      if (minor === null) return prev;
      return { ...prev, [key]: { currency, amount: minor.toString() } };
    });
  }

  async function save(): Promise<void> {
    if (form === null) return;
    setSaving(true);
    try {
      const patch: SetTreasuryThresholdsRequest = {
        sandbox: form.sandbox,
        usdtLowThreshold: form.usdtLowThreshold,
        ngnFloatLowThreshold: form.ngnFloatLowThreshold,
        manualReviewCeiling: form.manualReviewCeiling,
        reconciliationToleranceBps: form.reconciliationToleranceBps,
        topUpWindowHours: form.topUpWindowHours,
        repriceToleranceBps: form.repriceToleranceBps,
        balanceMaxAgeSeconds: form.balanceMaxAgeSeconds,
        conversionsPaused: form.conversionsPaused,
        pausedReason: form.pausedReason,
      };
      const saved = await api.setTreasuryThresholds(patch);
      setForm(saved);
      toast.push('success', 'Limits saved.');
    } catch (error) {
      toast.push('error', 'Those limits were not saved.', messageOf(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Trading limits</h1>
        <p className="page-head__sub">
          The limits that decide when a trade is refused, and the two switches that stop trading altogether.
        </p>
      </div>

      <Card title="The two switches that stop trading">
        <Switch
          checked={form.sandbox}
          disabled={!canWrite}
          onChange={(next) => {
            // One-sided confirm: turning sandbox *off* is the moment real coin starts moving.
            // Turning it back on only ever stops vendors being called, so it needs no ceremony.
            if (!next && !window.confirm(SANDBOX_OFF_CONFIRM)) return;
            set('sandbox', next);
          }}
          label="Sandbox"
          title="Practice mode"
        />
        <p className="muted">
          {form.sandbox
            ? 'ON. Trades are written to your own records only. Your crypto provider is never contacted and no real coin moves.'
            : 'OFF. Trades reach your crypto provider and real coin is bought and sold.'}
        </p>
        <Switch
          checked={form.conversionsPaused}
          disabled={!canWrite}
          onChange={(next) => set('conversionsPaused', next)}
          label="Stop all trading"
          title="Stop all trading"
        />
        <p className="muted">Stops all automatic trading straight away, for every customer.</p>
        {form.conversionsPaused && (
          <Field label="Why">
            {/*
              `pausedAt` is NOT on this endpoint — it rides `treasuryOverviewResponseSchema`, and
              the dashboard already renders it. Adding it here would mean widening a response
              schema for a nicety, so the copy points at the place that has it instead.

              It is worth knowing why this matters: reconciliation halts trading by itself and
              writes its own sentence into this box, so an operator can meet a reason like
              "reconciliation … found a delta past tolerance" having never touched the switch.
            */}
            <p className="muted">
              Recorded against the pause. The dashboard shows when trading stopped.
            </p>
            <Input
              value={form.pausedReason}
              disabled={!canWrite}
              maxLength={255}
              onChange={(e) => set('pausedReason', e.target.value)}
            />
          </Field>
        )}
      </Card>

      <Card title="Warn me when money runs low">
        <Field label="Warn me when free USDT falls below">
          <p className="muted">When your free USDT drops under this, the dashboard shows a warning. It does not stop anything.</p>
          <Input
            inputMode="decimal"
            disabled={!canWrite}
            value={minorUnitsToInput(form.usdtLowThreshold.amount, decimalsOf('USDT'))}
            onChange={(e) => setMoney('usdtLowThreshold', e.target.value)}
          />
        </Field>
        <Field label="Warn me when naira falls below">
          <p className="muted">When the naira you can pay out from drops under this, the dashboard shows a warning.</p>
          <Input
            inputMode="decimal"
            disabled={!canWrite}
            value={minorUnitsToInput(form.ngnFloatLowThreshold.amount, 2)}
            onChange={(e) => setMoney('ngnFloatLowThreshold', e.target.value)}
          />
        </Field>
        <Field label="Treat balances older than this as unknown (seconds)">
          <p className="muted">If the balance we last read from your crypto provider is older than this, we treat it as unknown and refuse trades rather than guess.</p>
          <Input
            type="number"
            min={10}
            max={3600}
            disabled={!canWrite}
            value={form.balanceMaxAgeSeconds}
            onChange={(e) => set('balanceMaxAgeSeconds', Number(e.target.value))}
          />
        </Field>
      </Card>

      <Card title="When a trade needs your approval">
        <Field label="Send trades bigger than this for approval">
          <p className="muted">Anything above this waits in the review queue instead of going through on its own.</p>
          <Input
            inputMode="decimal"
            disabled={!canWrite}
            value={minorUnitsToInput(form.manualReviewCeiling.amount, 2)}
            onChange={(e) => setMoney('manualReviewCeiling', e.target.value)}
          />
        </Field>
        <Field label="How far the price may move before you are asked">
          <p className="muted">If the price moves more than this between the quote and the payment arriving, the order waits for your decision instead of going through. Typed in hundredths of a percent, so 100 means 1%.</p>
          <Input
            type="number"
            min={0}
            max={10000}
            disabled={!canWrite}
            value={form.repriceToleranceBps}
            onChange={(e) => set('repriceToleranceBps', Number(e.target.value))}
          />
        </Field>
        <Field label="How long to wait for the rest of an underpayment (hours)">
          <p className="muted">If a customer sends less than they agreed, this is how long they have to send the difference before the order is closed.</p>
          <Input
            type="number"
            min={1}
            max={168}
            disabled={!canWrite}
            value={form.topUpWindowHours}
            onChange={(e) => set('topUpWindowHours', Number(e.target.value))}
          />
        </Field>
        <Field label="Ignore differences smaller than this">
          <p className="muted">Tiny rounding differences are normal. Anything under this is not flagged on the balance check. Typed in hundredths of a percent.</p>
          <Input
            type="number"
            min={0}
            max={10000}
            disabled={!canWrite}
            value={form.reconciliationToleranceBps}
            onChange={(e) => set('reconciliationToleranceBps', Number(e.target.value))}
          />
        </Field>
      </Card>

      {canWrite ? (
        <Button onClick={() => void save()} loading={saving} variant="primary">
          Save limits
        </Button>
      ) : (
        <p className="muted">Only the finance role can change these. You can see them but not edit.</p>
      )}
    </>
  );
}

/**
 * The loudest thing on this page, and the one string on it the plain-language pass missed.
 *
 * Every other sentence here was rewritten out of house vocabulary; this dialog still said
 * "the custodian", "bridge legs" and "settling on our own books" — three terms the operator
 * running this business has no reason to know, in the one place where not understanding the
 * sentence costs real money. It now says the same thing in the words the switch above it uses.
 */
const SANDBOX_OFF_CONFIRM = [
  'Turn sandbox off?',
  '',
  'From this point every trade reaches your crypto provider and real coin is bought and sold with real customer money. Until now trades have only been written to your own records and no provider was contacted.',
  '',
  'Before you do this, make sure your crypto provider has money in it and its login is switched on under Provider logins.',
].join('\n');
